import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchTeachers } from '../../store/slices/teachersSlice';
import { generateMockTeachers } from './mockTeachers';
import { getLanguagesApi, getCountriesApi } from '../../api/teachers';

import { TeacherFilterBar } from './components/TeacherFilterBar';
import { TeacherCardGrid } from './components/TeacherCards';
import { TeacherGridSkeletons } from './components/TeacherSkeletons';
import { TeacherPagination } from './components/TeacherPagination';

import './Teachers.scss';

// ── Constants ─────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 16;
const FEATURED_COUNT = 6;
const SORT_OPTIONS = [
  'Most Popular',
  'Newest',
  'Highest Rated',
  'Price: Low to High',
  'Price: High to Low',
];

/** Number of popular tags to show under the search bar */
const POPULAR_TAGS_LIMIT = 8;

const FALLBACK_TEACHERS = generateMockTeachers(120);

// ── Helpers ────────────────────────────────────────────────────────

/**
 * Extract all unique languages from teacher list sorted by frequency.
 * Returns a plain string[].
 */
const extractLanguages = (teachers) => {
  const counts = {};
  teachers.forEach((t) => {
    const langs = t.user_languages
      ? t.user_languages.map(l => typeof l === 'object' ? l.name : l)
      : (Array.isArray(t.languages) ? t.languages : (typeof t.languages === 'string' ? t.languages.split(',') : []));

    langs.forEach((lang) => {
      const trimmed = lang ? String(lang).trim() : '';
      if (trimmed) counts[trimmed] = (counts[trimmed] || 0) + 1;
    });
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([lang]) => lang);
};

/**
 * Extract unique countries from teacher list with flag emoji.
 * Returns { value, label, flag }[].
 */
const extractCountries = (teachers) => {
  const seen = new Map();
  teachers.forEach((t) => {
    let name = '';
    let flag = '';

    if (t.country && typeof t.country === 'object') {
      name = t.country.name || '';
      flag = t.country.flag || t.country.country_flag || '';
    } else {
      name = t.country || t.country_name || '';
    }

    if (!flag) {
      flag = t.country_flag || t.flag || '';
    }

    const nameStr = typeof name === 'string' ? name : String(name || '');
    if (nameStr && !seen.has(nameStr)) {
      seen.set(nameStr, { value: nameStr, label: nameStr, flag });
    }
  });
  return Array.from(seen.values());
};

/**
 * Derive popular tags = top N languages + skills by frequency.
 */
const extractPopularTags = (teachers, limit = 8) => {
  const counts = {};
  teachers.forEach((t) => {
    [...(t.languages || []), ...(t.skills || t.tags || [])].forEach((tag) => {
      if (tag) counts[tag] = (counts[tag] || 0) + 1;
    });
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tag]) => tag);
};

/**
 * Derive sidebar fields = unique skills/tags with counts.
 */
// ── Component ──────────────────────────────────────────────────────

/**
 * Teachers
 * Full teacher listing page.
 * All filter options (languages, countries, tags, fields) are derived
 * dynamically from the fetched teacher data — zero hardcoded values.
 */
export const Teachers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ── Redux ──────────────────────────────────────────────────────
  const { teachers: apiTeachers, pagination, loading } = useSelector((s) => s.teachers);
  const { isAuthenticated } = useSelector((state) => state.auth || {});

  const hasToken = localStorage.getItem('authToken') && localStorage.getItem('authToken') !== 'null' && localStorage.getItem('authToken') !== 'undefined';

  // ── Filter state ───────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [activeLanguage, setActiveLanguage] = useState('');
  const [activeCountry, setActiveCountry] = useState('');
  const [activeSpecialty, setActiveSpecialty] = useState('');
  const [activeAvailability, setActiveAvailability] = useState('');
  const [sortBy, setSortBy] = useState('Most Popular');
  const [currentPage, setCurrentPage] = useState(1);
  const [languagesList, setLanguagesList] = useState([]);
  const [countriesList, setCountriesList] = useState([]);

  // ── Fetch on mount ─────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchTeachers({}));

    // Fetch languages list from backend database
    getLanguagesApi()
      .then((res) => {
        if (res && res.data) {
          const list = res.data.map(l => l.name).filter(Boolean);
          setLanguagesList(list);
        }
      })
      .catch((err) => console.error("Failed to fetch languages list:", err));

    // Fetch countries list from backend database
    getCountriesApi()
      .then((res) => {
        if (res && res.data) {
          const list = res.data.map(c => ({
            value: c.name,
            label: c.name,
            flag: ''
          })).filter(c => c.value);
          setCountriesList(list);
        }
      })
      .catch((err) => console.error("Failed to fetch countries list:", err));
  }, [dispatch]);

  // ── Debounced re-fetch on search / language change ─────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchTeachers({ search, language: activeLanguage }));
    }, 420);
    return () => clearTimeout(timer);
  }, [search, activeLanguage, dispatch]);

  // ── Base teacher list (API → fallback mock) ────────────────────
  const teacherList = useMemo(
    () => (apiTeachers.length > 0 ? apiTeachers : !loading ? FALLBACK_TEACHERS : []),
    [apiTeachers, loading]
  );

  // ── Dynamically derived specialties list ────────────────────────
  const specialtiesList = useMemo(() => {
    const counts = {};
    teacherList.forEach((t) => {
      const skills = t.skills || t.tags || [];
      skills.forEach((s) => {
        if (s) counts[s] = (counts[s] || 0) + 1;
      });
    });
    return Object.keys(counts).sort();
  }, [teacherList]);

  // ── Client-side filtering + sorting ───────────────────────────
  const filteredTeachers = useMemo(() => {
    let list = [...teacherList];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter((t) => {
        const fullName = t.full_name || t.name || '';
        const countryName = typeof t.country === 'object' ? t.country?.name : (t.country || t.country_name || '');
        const countryStr = typeof countryName === 'string' ? countryName : String(countryName || '');

        const langs = t.user_languages
          ? t.user_languages.map(l => typeof l === 'object' ? l.name : l)
          : (Array.isArray(t.languages) ? t.languages : (typeof t.languages === 'string' ? t.languages.split(',') : []));

        const skills = t.skills || t.tags || [];

        return (
          fullName.toLowerCase().includes(q) ||
          langs.some((l) => String(l || '').toLowerCase().includes(q)) ||
          skills.some((s) => String(s || '').toLowerCase().includes(q)) ||
          countryStr.toLowerCase().includes(q)
        );
      });
    }

    if (activeLanguage) {
      list = list.filter((t) => {
        const langs = t.user_languages
          ? t.user_languages.map(l => typeof l === 'object' ? l.name : l)
          : (Array.isArray(t.languages) ? t.languages : (typeof t.languages === 'string' ? t.languages.split(',') : []));
        return langs.some((l) => String(l || '').toLowerCase() === activeLanguage.toLowerCase());
      });
    }

    if (activeCountry) {
      list = list.filter((t) => {
        const countryName = typeof t.country === 'object' ? t.country?.name : (t.country || t.country_name || '');
        const countryStr = typeof countryName === 'string' ? countryName : String(countryName || '');
        return countryStr.toLowerCase() === activeCountry.toLowerCase();
      });
    }

    if (activeSpecialty) {
      list = list.filter((t) => {
        const skills = t.skills || t.tags || [];
        return skills.some((s) => String(s || '').toLowerCase() === activeSpecialty.toLowerCase());
      });
    }

    if (activeAvailability) {
      if (activeAvailability === 'online') {
        list = list.filter((t) => t.is_online);
      } else if (activeAvailability === 'away') {
        list = list.filter((t) => t.is_away);
      }
    }

    // Sort
    switch (sortBy) {
      case 'Most Popular':
        list.sort((a, b) => (b.follower_count || 0) - (a.follower_count || 0));
        break;
      case 'Newest':
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'Highest Rated':
        list.sort((a, b) => (b.rating || b.avg_rating || 0) - (a.rating || a.avg_rating || 0));
        break;
      case 'Price: Low to High':
        list.sort((a, b) => (a.lesson_price || a.price || 0) - (b.lesson_price || b.price || 0));
        break;
      case 'Price: High to Low':
        list.sort((a, b) => (b.lesson_price || b.price || 0) - (a.lesson_price || a.price || 0));
        break;
      default:
        break;
    }

    return list;
  }, [teacherList, search, activeLanguage, activeCountry, activeSpecialty, activeAvailability, sortBy]);

  // ── Pagination ─────────────────────────────────────────────────
  const totalPages = Math.ceil(filteredTeachers.length / ITEMS_PER_PAGE) || 1;

  const paginatedTeachers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTeachers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTeachers, currentPage]);

  // Reset page whenever any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeLanguage, activeCountry, activeSpecialty, activeAvailability, sortBy]);

  // ── Handlers ──────────────────────────────────────────────────
  const handleClear = useCallback(() => {
    setSearch('');
    setActiveLanguage('');
    setActiveCountry('');
    setActiveSpecialty('');
    setActiveAvailability('');
    setSortBy('Most Popular');
  }, []);

  const navigateToTeacher = useCallback(
    (id) => navigate('/teachers-details', { state: { id } }),
    [navigate]
  );

  const totalTeachers = apiTeachers.length > 0 ? (pagination?.total || apiTeachers.length) : FALLBACK_TEACHERS.length;
  const totalCountries = countriesList.length > 0 ? countriesList.length : 15;
  const totalStudents = apiTeachers.length > 0
    ? apiTeachers.reduce((sum, t) => sum + (t.follower_count || t.students || 0), 0) || 10450
    : 10450;

  const formatCount = (num, isK = false) => {
    if (!num || isNaN(num)) return '0+';
    if (isK && num >= 1000) {
      return Math.round(num / 1000) + 'K+';
    }
    return num.toLocaleString() + '+';
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="teachers-page">

      {/* Hero Header Section */}
      <section className="tp-header-section">
        <div className="container-xl">
          <div className="row g-4 align-items-center">
            {/* Hero Left Column */}
            <div className="col-lg-5 col-xl-5">
              <h1 className="tp-new-hero__title">
                Find the perfect <span className="blue-text">teacher</span> for you
              </h1>
              <p className="tp-new-hero__subtitle">
                Connect with professional teachers from around the world. Learn, speak and grow your skills your way.
              </p>
              <div className="tp-new-hero__stats">
                <div className="tp-new-hero__stat-badge">
                  <span className="tp-new-hero__stat-icon bg-blue">
                    <i className="bi bi-people-fill" />
                  </span>
                  <div>
                    <span className="num">{formatCount(totalTeachers)}</span>
                    <span className="label">Expert Teachers</span>
                  </div>
                </div>
                <div className="tp-new-hero__stat-badge">
                  <span className="tp-new-hero__stat-icon bg-light-blue">
                    <i className="bi bi-globe" />
                  </span>
                  <div>
                    <span className="num">{formatCount(totalCountries)}</span>
                    <span className="label">Countries</span>
                  </div>
                </div>
                <div className="tp-new-hero__stat-badge">
                  <span className="tp-new-hero__stat-icon bg-orange">
                    <i className="bi bi-star-fill" />
                  </span>
                  <div>
                    <span className="num">{formatCount(totalStudents, true)}</span>
                    <span className="label">Happy Students</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Right Column (Filters card) */}
            <div className="col-lg-7 col-xl-7">
              <TeacherFilterBar
                search={search}
                onSearch={setSearch}
                languages={languagesList}
                countries={countriesList}
                specialties={specialtiesList}
                activeLanguage={activeLanguage}
                onLanguage={setActiveLanguage}
                activeCountry={activeCountry}
                onCountry={setActiveCountry}
                activeSpecialty={activeSpecialty}
                onSpecialty={setActiveSpecialty}
                activeAvailability={activeAvailability}
                onAvailability={setActiveAvailability}
                sortBy={sortBy}
                onSortBy={setSortBy}
                onClear={handleClear}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="tp-main">
        <div className="container-xl">
          {/* Results Header: Showing X-Y of Z and Grid/List view toggle */}
          <div className="tp-results-header">
            <span className="tp-results-count">
              Showing {filteredTeachers.length > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0}-
              {Math.min(filteredTeachers.length, currentPage * ITEMS_PER_PAGE)} of {filteredTeachers.length} teachers
            </span>
            <div className="tp-view-toggle">
              <button className="tp-view-toggle__btn active" aria-label="Grid View">
                <i className="bi bi-grid-fill" style={{ marginRight: '6px' }} /> Grid
              </button>
              <button className="tp-view-toggle__btn" aria-label="List View">
                <i className="bi bi-list" style={{ marginRight: '6px' }} /> List
              </button>
            </div>
          </div>

          {/* Loading */}
          {loading && <TeacherGridSkeletons count={ITEMS_PER_PAGE} />}

          {/* Empty State */}
          {!loading && paginatedTeachers.length === 0 && (
            <div className="tp-empty">
              <div className="tp-empty__icon">🔍</div>
              <h4 className="tp-empty__title">No teachers found</h4>
              <p className="tp-empty__desc">
                Try adjusting your search or clearing some filters.
              </p>
            </div>
          )}

          {/* Grid list of teachers (6-column layout on extra large viewports) */}
          {!loading && paginatedTeachers.length > 0 && (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-4 g-3">
              {paginatedTeachers.map((teacher) => (
                <div className="col" key={teacher._id}>
                  <TeacherCardGrid
                    teacher={teacher}
                    onView={() => navigateToTeacher(teacher._id)}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          <TeacherPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />

          {/* CTA Banner: Become a Teacher */}
          <div className="tp-cta-banner">
            <div className="tp-cta-banner__left">
              <div className="tp-cta-banner__icon-wrap">
                <i className="bi bi-mortarboard-fill" />
              </div>
              <div className="tp-cta-banner__text">
                <h3>Become a Teacher on Yoospeak</h3>
                <p>Share your knowledge, connect with students and earn on your own schedule.</p>
              </div>
            </div>
            <button className="tp-cta-banner__btn">
              Join as a Teacher
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};
